// calcolo della durata dei timeslot 

export const parseTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

export const getShiftDuration = (shift) => {
  const workStart = parseTime(shift.workStartTime);
  const workEnd = parseTime(shift.workEndTime);
  const totalMinutes = workEnd - workStart;
  
  const breakMinutes = shift.breaks?.reduce((sum, b) => {
      const start = parseTime(b.start_at);
      const end = parseTime(b.end_at);
      return sum + (end - start);
  }, 0) || 0;

  return (totalMinutes - breakMinutes) / 60;
};

export const getShiftDurationWithMidnight = (shift) => {
  const start = parseTime(shift.workStartTime);
  const end = parseTime(shift.workEndTime);

  let totalMinutes;

  if (end >= start) {
    totalMinutes = end - start;
  } else {
    totalMinutes = (24 * 60 - start) + end;
  }

  const breakMinutes = (shift.breaks || []).reduce((sum, b) => {
    const bStart = parseTime(b.start_at);
    const bEnd = parseTime(b.end_at);

    let bDur;
    if (bEnd >= bStart) {
      bDur = bEnd - bStart;
    } else {
      bDur = (24 * 60 - bStart) + bEnd;
    }

    return sum + bDur;
  }, 0);

  return (totalMinutes - breakMinutes) / 60;
};

export const getTotalWeeklyHours = (daysOfWeek, shiftsByDay) => {
  return daysOfWeek.reduce((total, day) => {
    const shifts = shiftsByDay[day] || [];
    const dayHours = shifts.reduce((sum, shift) => sum + getShiftDuration(shift), 0);
    return total + dayHours;
  }, 0);
};

export const getTimeSlotsDuration = (timeSlots) => {
  let totalMinutes = 0;

  timeSlots.forEach(slot => {
    const start = parseTime(slot.start_at);
    const end = parseTime(slot.end_at);
    let duration = end - start;

    const breakMinutes =
      (slot.breaks?.reduce((sum, b) => {
          const bStart = parseTime(b.start_at);
          const bEnd = parseTime(b.end_at);
          return sum + (bEnd - bStart);
        }, 0)) ?? 0;

    duration -= breakMinutes;
    totalMinutes += duration;
  });

  return totalMinutes / 60;
};

export const getTotalWeeklyHoursFromSlots = (slotsByDay) => {
  let totalMinutes = 0;

  slotsByDay.forEach((slot) => {
    if (!slot) return;

    if (Array.isArray(slot.time_slots)) {
      totalMinutes += slot.time_slots.reduce((sum, ts) => sum + (ts.duration || 0), 0);
    }

    if (slot.working_hour && Array.isArray(slot.working_hour.time_slots)) {
      totalMinutes += slot.working_hour.time_slots.reduce((sum, ts) => sum + (ts.duration || 0), 0);
    }
  });

  return (totalMinutes / 60);
};


export const formatHoursAndMinutes = (hoursDecimal) => {
  const totalMinutes = Math.round(hoursDecimal * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0 && minutes === 0) {
    return '0 min';
  } else if (hours === 0) {
    return `${minutes} min`;
  } else if (minutes === 0) {
    return `${hours} ore`;
  } else {
    return `${hours} ore, ${minutes} min`;
  }
};


export const getTotalWeeklyDurationFromDays = (slotsByDay) => {
  return slotsByDay.reduce((total, daySlot) => {
    if (daySlot?.duration && typeof daySlot.duration === 'number') {
      return total + daySlot.duration;
    }
    return total;
  }, 0) / 60;
};


export const buildSlotsByDateFromPreviews = (baseDate, shiftPreviews = []) => {
    const baseISO = normalizeToISO(baseDate);

    if (!baseISO) return {};

    const result = {};

    const ensureArrayForDate = (iso) => {
        if (!result[iso]) {
            result[iso] = [];
        }

        return result[iso];
    };

    shiftPreviews.forEach((preview) => {
        const {
            workStartTime,
            workEndTime,
            breaks = [],
            crossesMidnight,
        } = preview || {};

        if (!workStartTime || !workEndTime) {
            return;
        }

        const isCross =
            crossesMidnight === true ||
            workEndTime < workStartTime;

        /*
         * =====================================================
         * TURNO NELLO STESSO GIORNO
         * =====================================================
         */
        if (!isCross) {
            const slot = {
                start_at: workStartTime,
                end_at: workEndTime,

                breaks: breaks
                    .filter(
                        (b) =>
                            b?.start_at &&
                            b?.end_at
                    )
                    .map((b) => ({
                        start_at: b.start_at,
                        end_at: b.end_at,
                        is_visible:
                            b?.is_visible ?? true,
                        confirmed:
                            b?.confirmed ?? true,
                    })),
            };

            ensureArrayForDate(baseISO).push(slot);

            return;
        }

        /*
         * =====================================================
         * TURNO CHE ATTRAVERSA LA MEZZANOTTE
         * =====================================================
         */

        const startMin = parseTimeToMinutes(workStartTime);

        const endMin = parseTimeToMinutes(workEndTime);

        const day1ISO = baseISO;

        const day2ISO = addDaysToISO(baseISO, 1);

        const slotDay1 = {
            start_at: workStartTime,
            end_at: '24:00',
            breaks: [],
        };

        const slotDay2 = {
            start_at: '00:00',
            end_at: workEndTime,
            breaks: [],
        };

        breaks.forEach((b) => {
            if (!b?.start_at || !b?.end_at) {
                return;
            }

            const bStart = parseTimeToMinutes(b.start_at);
            const bEnd = parseTimeToMinutes(b.end_at);

            const makeBreak = (startMinutes, endMinutes) => ({
                start_at: minutesToTime(startMinutes),
                end_at: minutesToTime(endMinutes),
                is_visible: b?.is_visible ?? true,
                confirmed: b?.confirmed ?? true,
            });

            /*
             * Pausa che NON attraversa mezzanotte.
             */
            if (bEnd >= bStart) {

                /*
                 * Parte serale:
                 *
                 * start turno → 24:00
                 */
                if (bStart >= startMin && bEnd <= 24 * 60) {
                    slotDay1.breaks.push(makeBreak(bStart, bEnd));
                    return;
                }

                /*
                 * Parte mattutina:
                 *
                 * 00:00 → fine turno
                 */
                if (bStart >= 0 && bEnd <= endMin) {
                  slotDay2.breaks.push(makeBreak(bStart, bEnd));
                }

                /*
                 * Se non rientra in nessuna delle due
                 * porzioni del turno, viene ignorata.
                 */
                return;
            }

            /*
             * Pausa che attraversa la mezzanotte.
             *
             * Esempio:
             *
             * 23:50 → 00:20
             */

            slotDay1.breaks.push(makeBreak(bStart, 24 * 60));
            slotDay2.breaks.push(makeBreak(0, bEnd));
        });

        ensureArrayForDate(day1ISO).push(slotDay1);
        ensureArrayForDate(day2ISO).push(slotDay2);
    });

    return result;
};
